/**
 * QueryBuilder Store - loop slice.
 *
 * Loop-step (FOR…ENDFOR) mutators: loop variable, iteration kind, field
 * source, excluded values (field mode) and literal values (literal mode).
 * All flow through `updateStep` → `mapStepInTree`, so nested loops work
 * without further plumbing.
 */
import { StateCreator } from "zustand"
import { LoopIteration } from "types/queryBuilder.types"
import { LoopSlice, QueryBuilderStore } from "../types"
import { updateStep } from "../helpers"

export const createLoopSlice: StateCreator<
  QueryBuilderStore,
  [["zustand/persist", unknown]],
  [],
  LoopSlice
> = (set) => ({
  // ---- Loop-specific mutators ----
  setLoopVariable: (stepId, variable) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "loop" ? { ...s, variable } : s
      )
    ),
  setLoopIterationKind: (stepId, kind) =>
    set((state) =>
      updateStep(state, stepId, (s) => {
        if (s.type !== "loop") return s
        if (kind === s.iteration.kind) return s
        // Switching modes wipes the previous mode's payload — keeping
        // stale `values` around when the user is on field mode (and
        // vice versa) just creates surprising output later.
        const next: LoopIteration =
          kind === "field"
            ? { kind: "field", source: "", excludedValues: ["None"] }
            : { kind: "literal", values: [] }
        return { ...s, iteration: next }
      })
    ),
  setLoopFieldSource: (stepId, source) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "loop" && s.iteration.kind === "field"
          ? { ...s, iteration: { ...s.iteration, source } }
          : s
      )
    ),
  setLoopExcludedValues: (stepId, values) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "loop" && s.iteration.kind === "field"
          ? {
              ...s,
              iteration: {
                ...s.iteration,
                excludedValues: Array.from(
                  new Set(values.map((v) => v.trim()).filter(Boolean))
                ),
              },
            }
          : s
      )
    ),
  addLoopExcludedValue: (stepId, value) =>
    set((state) =>
      updateStep(state, stepId, (s) => {
        if (s.type !== "loop" || s.iteration.kind !== "field") return s
        const trimmed = value.trim()
        if (!trimmed || s.iteration.excludedValues.includes(trimmed)) {
          return s
        }
        return {
          ...s,
          iteration: {
            ...s.iteration,
            excludedValues: [...s.iteration.excludedValues, trimmed],
          },
        }
      })
    ),
  removeLoopExcludedValue: (stepId, value) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "loop" && s.iteration.kind === "field"
          ? {
              ...s,
              iteration: {
                ...s.iteration,
                excludedValues: s.iteration.excludedValues.filter(
                  (v) => v !== value
                ),
              },
            }
          : s
      )
    ),
  addLoopLiteralValue: (stepId, value) =>
    set((state) =>
      updateStep(state, stepId, (s) => {
        if (s.type !== "loop" || s.iteration.kind !== "literal") return s
        const trimmed = value.trim()
        if (!trimmed || s.iteration.values.includes(trimmed)) return s
        return {
          ...s,
          iteration: {
            ...s.iteration,
            values: [...s.iteration.values, trimmed],
          },
        }
      })
    ),
  removeLoopLiteralValue: (stepId, value) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "loop" && s.iteration.kind === "literal"
          ? {
              ...s,
              iteration: {
                ...s.iteration,
                values: s.iteration.values.filter((v) => v !== value),
              },
            }
          : s
      )
    ),
  setLoopLiteralValues: (stepId, values) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "loop" && s.iteration.kind === "literal"
          ? { ...s, iteration: { ...s.iteration, values: [...values] } }
          : s
      )
    ),
})
